import { Check, Ban, Trash2, MonitorSmartphone, BadgeCheck, Network } from "lucide-react";
import { TrustedIps } from "./TrustedIps";
import { toast } from "sonner";
import { api, errMsg, getDeviceId } from "../../lib/api";

const STYLE = { approved: "bg-emerald-50 text-emerald-700 border-emerald-200", pending: "bg-amber-50 text-amber-800 border-amber-200", revoked: "bg-red-50 text-red-700 border-red-200" };
const when = (s) => new Date(s).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

export const DevicesTab = ({ devices, ips, reload }) => {
  const mine = getDeviceId();
  const trusted = new Set(ips.map((t) => t.ip));
  const trustIp = async (d) => {
    try {
      const r = await api.post("/trusted-ips", { ip: d.ip, label: `Approved from ${d.user_name}'s device` });
      toast.success(`IP ${r.data.ip} approved${r.data.devices_approved ? ` · ${r.data.devices_approved} device(s) approved` : ""}`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const act = async (d, status) => {
    if (status === "delete" && !window.confirm(`Remove ${d.label || "this device"} for ${d.user_name}? They will need approval again.`)) return;
    try {
      if (status === "delete") await api.delete(`/devices/${d.id}`);
      else await api.patch(`/devices/${d.id}`, { status });
      toast.success(status === "delete" ? "Device removed" : `Device ${status}`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const sorted = [...devices].sort((a, b) => (a.status === "pending" ? -1 : 0) - (b.status === "pending" ? -1 : 0));

  return (
    <div>
    <TrustedIps ips={ips} reload={reload} />
    <div className="glass rounded-3xl overflow-hidden" data-testid="devices-tab">
      <div className="p-5">
        <h3 className="text-lg font-bold text-emerald-950">Device approvals</h3>
        <p className="text-xs text-slate-500">Every new browser or tablet must be approved before it can sign in. Management's first device is auto-approved.</p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] text-sm">
          <thead><tr className="bg-emerald-950/[0.03] text-left">{["Device", "User", "IP", "First seen", "Last seen", "Status", "Actions"].map((h) => <th key={h} className="px-5 py-3 field-label">{h}</th>)}</tr></thead>
          <tbody>
            {sorted.length === 0 && <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-500">No devices registered yet.</td></tr>}
            {sorted.map((d) => (
              <tr key={d.id} data-testid={`device-row-${d.id}`} className={`border-t border-emerald-900/5 ${d.status === "pending" ? "bg-amber-50/40" : ""}`}>
                <td className="px-5 py-3">
                  <div className="flex items-center gap-2 font-semibold text-emerald-950"><MonitorSmartphone className="h-4 w-4 text-emerald-700" /> {d.label || "Unknown device"}</div>
                  <div className="mt-1 flex gap-1">
                    {d.auto_approved && <span className="inline-flex items-center gap-1 rounded-full bg-emerald-900 text-amber-50 px-2 py-0.5 text-[10px] font-semibold"><BadgeCheck className="h-3 w-3" /> Auto-approved</span>}
                    {d.approved_via === "ip" && <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-800 px-2 py-0.5 text-[10px] font-semibold"><Network className="h-3 w-3" /> Approved by IP</span>}
                    {d.device_id === mine && <span className="rounded-full bg-amber-100 text-amber-800 px-2 py-0.5 text-[10px] font-semibold">This device</span>}
                  </div>
                </td>
                <td className="px-5 py-3"><div className="font-semibold">{d.user_name}</div><div className="text-xs text-slate-500">{d.user_email}</div></td>
                <td className="px-5 py-3 text-xs text-slate-600">
                  <div>{d.ip || "—"}</div>
                  {d.ip && (trusted.has(d.ip)
                    ? <span className="text-[10px] font-semibold text-sky-700">Approved IP</span>
                    : <button data-testid={`device-trust-ip-button-${d.id}`} onClick={() => trustIp(d)} className="mt-1 text-[11px] font-semibold text-emerald-800 hover:underline">Approve this IP</button>)}
                </td>
                <td className="px-5 py-3 text-xs text-slate-600">{when(d.first_seen)}</td>
                <td className="px-5 py-3 text-xs text-slate-600">{when(d.last_seen)}</td>
                <td className="px-5 py-3"><span data-testid={`device-status-${d.id}`} className={`rounded-full border px-2.5 py-1 text-xs font-semibold capitalize ${STYLE[d.status]}`}>{d.status}</span></td>
                <td className="px-5 py-3">
                  <div className="flex gap-2">
                    {d.status !== "approved" && <button data-testid={`device-approve-button-${d.id}`} onClick={() => act(d, "approved")} className="inline-flex items-center gap-1 rounded-full bg-emerald-900 px-3 py-1.5 text-xs font-semibold text-amber-50 hover:bg-emerald-800 transition-colors"><Check className="h-3.5 w-3.5" /> Approve</button>}
                    {d.status === "approved" && <button data-testid={`device-revoke-button-${d.id}`} onClick={() => act(d, "revoked")} className="inline-flex items-center gap-1 rounded-full border border-red-200 bg-white px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-50 transition-colors"><Ban className="h-3.5 w-3.5" /> Revoke</button>}
                    <button data-testid={`device-delete-button-${d.id}`} onClick={() => act(d, "delete")} className="h-8 w-8 grid place-items-center rounded-full text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors" title="Remove device"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
};
